/**
 * Workspaces page: launcher, active session, and recent session history.
 */

import { type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  WorkspaceStatusBadge,
  SessionCountdown,
  LauncherToolbar,
  useDeleteWorkspace,
  useWorkspaces,
} from '@features/workspace';
import { LoginsPanel } from '@features/logins/components/LoginsPanel';
import { LoginStatusPills } from '@features/logins/components/LoginStatusPill';
import { Toolbar } from '@components/ui/Toolbar';
import { ROUTES } from '@utils/constants';

export function WorkspacesPage(): ReactElement {
  const navigate = useNavigate();

  const { data: workspaces, isLoading, error } = useWorkspaces();
  const { mutateAsync: deleteWorkspace, isPending: isDeleting } = useDeleteWorkspace();

  // Find active workspace (building, starting, or running)
  const activeWorkspace = workspaces?.find(
    (w) => w.status === 'building' || w.status === 'starting' || w.status === 'running'
  );

  // Recent terminated workspaces (for history)
  const recentWorkspaces = workspaces?.filter(
    (w) => w.status === 'terminated' || w.status === 'error'
  ).slice(0, 5);

  const handleOpenWorkspace = (workspaceId: string): void => {
    void navigate(`${ROUTES.WORKSPACE}/${workspaceId}`);
  };

  const handleDeleteWorkspace = async (workspaceId: string): Promise<void> => {
    try {
      await deleteWorkspace(workspaceId);
    } catch (err) {
      console.error('Failed to delete workspace:', err);
    }
  };

  return (
    <div className="workspaces-page">
      <div className="workspaces-page__container">
        {isLoading && (
          <div className="loading-message">Loading workspaces...</div>
        )}

        {error && (
          <div className="error-message">{error.message}</div>
        )}

        {/* Active Workspace */}
        {activeWorkspace && (
          <Toolbar
            info={<>
              <WorkspaceStatusBadge status={activeWorkspace.status} />
              <LoginStatusPills compact />
              <span className="app-toolbar__project">{activeWorkspace.project_name}</span>
              <span className="app-toolbar__branch">({activeWorkspace.branch})</span>
            </>}
            actions={<>
              {activeWorkspace.status === 'running' && (
                <SessionCountdown workspaceId={activeWorkspace.id} expiresAt={activeWorkspace.expires_at} />
              )}
              {activeWorkspace.status === 'running' && activeWorkspace.url != null && (
                <button
                  type="button"
                  onClick={() => { handleOpenWorkspace(activeWorkspace.id); }}
                  className="app-toolbar__primary"
                >
                  Open
                </button>
              )}
              <button
                type="button"
                onClick={() => { void handleDeleteWorkspace(activeWorkspace.id); }}
                disabled={isDeleting}
                className="app-toolbar__danger"
              >
                {isDeleting ? 'Stopping...' : 'Stop'}
              </button>
            </>}
          />
        )}

        {/* Inline Workspace Launcher (when no active workspace) */}
        {!isLoading && !activeWorkspace && <LauncherToolbar />}

        {/* Brief gates delivered-credentials copy on the workspace being RUNNING. */}
        <LoginsPanel hasActiveWorkspace={activeWorkspace?.status === 'running'} />

        {/* Recent Workspaces */}
        {recentWorkspaces && recentWorkspaces.length > 0 && (
          <section className="recent-workspaces">
            <h2 className="recent-workspaces__title">Recent Sessions</h2>
            <div className="recent-workspaces__list">
              {recentWorkspaces.map((workspace) => (
                <div key={workspace.id} className="recent-workspaces__item">
                  <div>
                    <span className="recent-workspaces__project">
                      {workspace.project_name}
                    </span>
                    <span className="recent-workspaces__branch">
                      ({workspace.branch})
                    </span>
                  </div>
                  <span className="recent-workspaces__date">
                    {new Date(workspace.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
